import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, FileCheck, Lock, Info } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import { emailService } from '@/services/emailService'

// Section wrapper
const Section = ({ id, children, className = "" }: { id: string; children: React.ReactNode; className?: string }) => (
  <section id={id} className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </section>
)

const ROI: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()
  
  const [step, setStep] = useState(1)
  const [focus, setFocus] = useState("")
  const [automationLevel, setAutomationLevel] = useState("Medium")
  const [submitted, setSubmitted] = useState(false)

  const [inputs, setInputs] = useState({
    volume: "",
    time: "",
    noshow: "",
    hourly: "48",
    consultEligible: "30",
    waitDays: "45",
    visitMinutes: "30",
    revPerPatient: "250000",
  })

  const [form, setForm] = useState({ name: "", email: "", org: "", phone: "" })

  const automationRates = {
    "Referral Management": { Low: 0.35, Medium: 0.5, High: 0.65 },
    "Appointment Scheduling": { Low: 0.35, Medium: 0.5, High: 0.65 },
    "Patient Self-Service": { Low: 0.35, Medium: 0.5, High: 0.65 },
    "Cell Therapy Planning": { Low: 0.35, Medium: 0.5, High: 0.65 },
    "Vaccination / Compliance Tracking": { Low: 0.35, Medium: 0.5, High: 0.65 },
    "eConsult": { Low: 0.35, Medium: 0.5, High: 0.65 },
  }

  const defaultTimeByFocus = {
    "Referral Management": 10,
    "Appointment Scheduling": 8,
    "Patient Self-Service": 5,
    "Cell Therapy Planning": 120,
    "Vaccination / Compliance Tracking": 6,
  }

  const throughputUpliftByLevel = { Low: 0.05, Medium: 0.1, High: 0.2 }

  const volume = Number(inputs.volume || 0)
  const timeMin = Number(inputs.time || defaultTimeByFocus[focus as keyof typeof defaultTimeByFocus] || 0)
  const noshow = Number(inputs.noshow || 0)
  const hourly = Number(inputs.hourly || 48)
  const dollarsPerMinute = hourly / 60
  const consultEligiblePct = Number(inputs.consultEligible || 30)
  const baselineWaitDays = Number(inputs.waitDays || 45)
  const visitMinutes = Number(inputs.visitMinutes || 30)
  const revPerPatient = Number(inputs.revPerPatient || 250000)

  const automationFactor = focus && automationRates[focus as keyof typeof automationRates]
    ? automationRates[focus as keyof typeof automationRates][automationLevel as keyof typeof automationRates["Referral Management"]]
    : 0.25

  const throughputUplift = throughputUpliftByLevel[automationLevel as keyof typeof throughputUpliftByLevel] || 0.1
  const needsTime = focus !== "eConsult"
  const isValid = volume > 0 && (!needsTime || timeMin > 0)

  const resultCards: Array<{ label: string; value: string; annual?: string }> = []

  if (step === 3) {
    if (["Referral Management", "Appointment Scheduling", "Vaccination / Compliance Tracking"].includes(focus)) {
      const minutesSaved = Math.max(volume * timeMin * (1 - noshow / 100) * automationFactor, 0)
      const costSaved = minutesSaved * dollarsPerMinute
      const annualMinutes = minutesSaved * 52
      const annualHours = annualMinutes / 60
      const annualCost = costSaved * 52
      resultCards.push(
        { label: t('roiCalculator.calculator.step3.cards.staffTimeSaved'), value: `${Math.round(minutesSaved)} min`, annual: `${Math.round(annualHours)} hrs` },
        { label: t('roiCalculator.calculator.step3.cards.laborSavings'), value: `$${Math.round(costSaved)}`, annual: `$${Math.round(annualCost)}` },
        { label: t('roiCalculator.calculator.step3.cards.automationLevel'), value: `${automationLevel} (${Math.round(automationFactor * 100)}%)` }
      )
    } else if (focus === "Patient Self-Service") {
      const staffShiftPct = automationFactor
      const shifted = volume * staffShiftPct
      const minutesSaved = Math.max(shifted * timeMin * (1 - noshow / 100), 0)
      const costSaved = minutesSaved * dollarsPerMinute
      const annualMinutes = minutesSaved * 52
      const annualHours = annualMinutes / 60
      const annualCost = costSaved * 52
      resultCards.push(
        { label: t('roiCalculator.calculator.step3.cards.selfScheduled'), value: `${Math.round(staffShiftPct * 100)}%` },
        { label: t('roiCalculator.calculator.step3.cards.staffTimeSaved'), value: `${Math.round(minutesSaved)} min`, annual: `${Math.round(annualHours)} hrs` },
        { label: t('roiCalculator.calculator.step3.cards.laborSavings'), value: `$${Math.round(costSaved)}`, annual: `$${Math.round(annualCost)}` }
      )
    } else if (focus === "eConsult") {
      const eligible = volume * (consultEligiblePct / 100)
      const resolved = eligible * automationFactor
      const slotsFreed = Math.max(resolved, 0)
      const remaining = Math.max(volume - resolved, 0)
      const reductionRate = volume > 0 ? slotsFreed / volume : 0
      const reductionPct = reductionRate * 100
      const effectiveWait = Math.max(baselineWaitDays * (1 - reductionRate), 0)
      const clinicHoursFreed = (slotsFreed * visitMinutes) / 60
      const annualSlots = Math.round(slotsFreed * 52)
      const annualClinicHours = (clinicHoursFreed * 52).toFixed(1)
      resultCards.push(
        { label: t('roiCalculator.calculator.step3.cards.slotsFreed'), value: `${Math.round(slotsFreed)}`, annual: `${annualSlots} slots` },
        { label: t('roiCalculator.calculator.step3.cards.clinicHoursFreed'), value: `${clinicHoursFreed.toFixed(1)} hrs`, annual: `${annualClinicHours} hrs` },
        { label: t('roiCalculator.calculator.step3.cards.waitlistReduction'), value: `${reductionPct.toFixed(1)}% (baseline ${baselineWaitDays}d → ~${Math.round(effectiveWait)}d)` },
        { label: t('roiCalculator.calculator.step3.cards.remainingCases'), value: `${Math.round(remaining)}` }
      )
    } else if (focus === "Cell Therapy Planning") {
      const minutesSaved = Math.max(volume * timeMin * automationFactor, 0)
      const addedPatients = Math.max(volume * throughputUplift, 0)
      const addedRevenue = addedPatients * revPerPatient
      const costSaved = minutesSaved * dollarsPerMinute
      const annualMinutes = minutesSaved * 52
      const annualHours = annualMinutes / 60
      const annualAddedPatients = Math.round(addedPatients * 52)
      const annualAddedRevenue = Math.round(addedRevenue * 52)
      const annualLabor = Math.round(costSaved * 52)
      resultCards.push(
        { label: t('roiCalculator.calculator.step3.cards.staffTimeSaved'), value: `${Math.round(minutesSaved)} min`, annual: `${Math.round(annualHours)} hrs` },
        { label: t('roiCalculator.calculator.step3.cards.additionalPatients'), value: `${addedPatients.toFixed(2)}`, annual: `${annualAddedPatients} patients` },
        { label: t('roiCalculator.calculator.step3.cards.addedRevenue'), value: `$${Math.round(addedRevenue)}`, annual: `$${annualAddedRevenue}` },
        { label: t('roiCalculator.calculator.step3.cards.laborSavings'), value: `$${Math.round(costSaved)}`, annual: `$${annualLabor}` }
      )
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      // Send to email service
      const result = await emailService.sendROICalculator({
        name: form.name,
        email: form.email,
        organization: form.org,
        phone: form.phone,
        focus,
        automationLevel,
        inputs
      }, 'roi_calculator_submit')
      
      if (result.success) {
        setSubmitted(true)
      } else {
        console.error('Failed to send ROI calculator form:', result.message)
        // Still show results but log the error
        setSubmitted(true)
      }
    } catch (error) {
      console.error('Error submitting ROI calculator form:', error)
      // Fallback: still show results
      setSubmitted(true)
    }
  }

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }

  return (
    <div className="min-h-screen w-full bg-white text-slate-800 overflow-y-auto">
      <header className="w-full text-white" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6" style={{fontFamily:"Montserrat, Inter, system-ui"}}>Eceptionist<span className="text-[#7AC943]">CX</span><sup className="text-2xl ml-1">™</sup></h2>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">{t('roiCalculator.hero.title')}</h1>
          <p className="text-base sm:text-lg opacity-95 max-w-3xl">{t('roiCalculator.hero.subtitle')}</p>
          <div className="mt-6 flex gap-3">
            <a href="#calculator" className="bg-white text-[#395091] px-6 py-3 rounded shadow">{t('roiCalculator.hero.startEstimate')}</a>
            <a href="#trusted" className="bg-white/10 border border-white/20 px-6 py-3 rounded text-white">{t('roiCalculator.hero.seeWhyTrusted')}</a>
          </div>
        </div>
      </header>

      <Section id="calculator" className="py-12">
        <div className="rounded-3xl bg-white p-6 shadow">
          {!submitted ? (
            <>
              {/* Step 1: Focus */}
              {step === 1 && (
                <div>
                  <h2 className="text-2xl font-bold mb-2">{t('roiCalculator.calculator.step1.title')}</h2>
                  <p className="text-slate-600 mb-6">{t('roiCalculator.calculator.step1.subtitle')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      t('roiCalculator.calculator.step1.options.referralManagement'),
                      t('roiCalculator.calculator.step1.options.appointmentScheduling'),
                      t('roiCalculator.calculator.step1.options.patientSelfService'),
                      t('roiCalculator.calculator.step1.options.cellTherapyPlanning'),
                      t('roiCalculator.calculator.step1.options.vaccinationCompliance'),
                      t('roiCalculator.calculator.step1.options.econsult')
                    ].map((item) => (
                      <button
                        key={item}
                        onClick={() => { setFocus(item); setStep(2); }}
                        className="p-4 rounded-xl border border-slate-200 hover:border-[#395091] hover:shadow text-left"
                      >
                        <span className="font-semibold">{item}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Inputs */}
              {step === 2 && (
                <div>
                  <h2 className="text-2xl font-bold mb-2">{t('roiCalculator.calculator.step2.title')}</h2>
                  <p className="text-slate-600 mb-6">
                    {focus === t('roiCalculator.calculator.step1.options.referralManagement') && t('roiCalculator.calculator.step2.descriptions.referralManagement')}
                    {focus === t('roiCalculator.calculator.step1.options.appointmentScheduling') && t('roiCalculator.calculator.step2.descriptions.appointmentScheduling')}
                    {focus === t('roiCalculator.calculator.step1.options.vaccinationCompliance') && t('roiCalculator.calculator.step2.descriptions.vaccinationCompliance')}
                    {focus === t('roiCalculator.calculator.step1.options.patientSelfService') && t('roiCalculator.calculator.step2.descriptions.patientSelfService')}
                    {focus === t('roiCalculator.calculator.step1.options.econsult') && t('roiCalculator.calculator.step2.descriptions.econsult')}
                    {focus === t('roiCalculator.calculator.step1.options.cellTherapyPlanning') && t('roiCalculator.calculator.step2.descriptions.cellTherapyPlanning')}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Volume */}
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-slate-700">
                        {focus === t('roiCalculator.calculator.step1.options.cellTherapyPlanning') 
                          ? t('roiCalculator.calculator.step2.labels.volumeCellTherapy')
                          : t('roiCalculator.calculator.step2.labels.volume')
                        }
                      </label>
                      <input 
                        type="number" 
                        min="0" 
                        className="w-full border rounded px-3 py-2" 
                        value={inputs.volume} 
                        onChange={(e) => setInputs({ ...inputs, volume: e.target.value })} 
                        placeholder="e.g., 150" 
                      />
                    </div>

                    {/* Time (not for eConsult) */}
                    {focus !== t('roiCalculator.calculator.step1.options.econsult') && (
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-slate-700">{t('roiCalculator.calculator.step2.labels.timePerUnit')}</label>
                        <select className="w-full border rounded px-3 py-2" value={inputs.time} onChange={(e) => setInputs({ ...inputs, time: e.target.value })}>
                          <option value="">Select…</option>
                          {[5,10,15,20,30,45,60,90,120,180,240,300,360,420,480,540,600,660,720,780,840,900,960,1020,1080].map(v => (
                            <option key={v} value={v}>{v} minutes</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Optional no-show / delay */}
                    {(focus === t('roiCalculator.calculator.step1.options.appointmentScheduling') || 
                      focus === t('roiCalculator.calculator.step1.options.patientSelfService') || 
                      focus === t('roiCalculator.calculator.step1.options.vaccinationCompliance')) && (
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-slate-700">{t('roiCalculator.calculator.step2.labels.noShowPercent')}</label>
                        <input 
                          type="number" 
                          min="0" 
                          max="100" 
                          className="w-full border rounded px-3 py-2" 
                          value={inputs.noshow} 
                          onChange={(e) => setInputs({ ...inputs, noshow: e.target.value })} 
                          placeholder="e.g., 8" 
                        />
                      </div>
                    )}

                    {/* Hourly (not for eConsult) */}
                    {focus !== t('roiCalculator.calculator.step1.options.econsult') && (
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-slate-700">{t('roiCalculator.calculator.step2.labels.hourlyRate')}</label>
                        <input 
                          type="number" 
                          min="0" 
                          step="1" 
                          className="w-full border rounded px-3 py-2" 
                          value={inputs.hourly} 
                          onChange={(e) => setInputs({ ...inputs, hourly: e.target.value })} 
                          placeholder="e.g., 48" 
                        />
                      </div>
                    )}

                    {/* eConsult specifics */}
                    {focus === t('roiCalculator.calculator.step1.options.econsult') && (
                      <>
                        <div className="flex flex-col gap-1">
                          <label className="text-sm font-medium text-slate-700">{t('roiCalculator.calculator.step2.labels.consultEligible')}</label>
                          <input 
                            type="number" 
                            min="0" 
                            max="100" 
                            className="w-full border rounded px-3 py-2" 
                            value={inputs.consultEligible} 
                            onChange={(e) => setInputs({ ...inputs, consultEligible: e.target.value })} 
                            placeholder="e.g., 30" 
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-sm font-medium text-slate-700">{t('roiCalculator.calculator.step2.labels.baselineWait')}</label>
                          <input 
                            type="number" 
                            min="0" 
                            className="w-full border rounded px-3 py-2" 
                            value={inputs.waitDays} 
                            onChange={(e) => setInputs({ ...inputs, waitDays: e.target.value })} 
                            placeholder="e.g., 45" 
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-sm font-medium text-slate-700">{t('roiCalculator.calculator.step2.labels.visitLength')}</label>
                          <input 
                            type="number" 
                            min="1" 
                            className="w-full border rounded px-3 py-2" 
                            value={inputs.visitMinutes} 
                            onChange={(e) => setInputs({ ...inputs, visitMinutes: e.target.value })} 
                            placeholder="e.g., 30" 
                          />
                        </div>
                      </>
                    )}

                    {/* Cell therapy specifics */}
                    {focus === t('roiCalculator.calculator.step1.options.cellTherapyPlanning') && (
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-slate-700">{t('roiCalculator.calculator.step2.labels.revenuePerPatient')}</label>
                        <input 
                          type="number" 
                          min="0" 
                          step="1000" 
                          className="w-full border rounded px-3 py-2" 
                          value={inputs.revPerPatient} 
                          onChange={(e) => setInputs({ ...inputs, revPerPatient: e.target.value })} 
                          placeholder="e.g., 250000" 
                        />
                      </div>
                    )}
                  </div>

                  {/* Automation Level */}
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold">{t('roiCalculator.calculator.step2.automationLevel.title')}</h3>
                      <span className="inline-flex items-center text-slate-500 text-sm" title={t('roiCalculator.calculator.step2.automationLevel.tooltip')}>
                        <Info className="w-4 h-4" />
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {["Low","Medium","High"].map(level => {
                        const pct = automationRates[focus as keyof typeof automationRates]?.[level as keyof typeof automationRates["Referral Management"]] ?? 0.25
                        return (
                          <button
                            key={level}
                            onClick={() => setAutomationLevel(level)}
                            title={`${level}: ${Math.round((pct||0)*100)}% automation`}
                            className={`px-4 py-2 rounded-lg border ${automationLevel === level ? "bg-[#395091] text-white border-[#395091]" : "border-slate-200 hover:bg-slate-50"}`}
                          >
                            {level}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-6">
                    <button onClick={() => setStep(1)} className="px-5 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition">{t('roiCalculator.calculator.step2.buttons.back')}</button>
                    <button onClick={() => { setFocus(""); setStep(1); }} className="px-5 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition">{t('roiCalculator.calculator.step2.buttons.changeFocus')}</button>
                    <button onClick={() => setStep(3)} disabled={!isValid} className={`px-6 py-3 rounded-lg shadow transition text-white ${isValid ? "bg-[#395091] hover:bg-[#2c3b6f]" : "bg-slate-300 cursor-not-allowed"}`}>{t('roiCalculator.calculator.step2.buttons.calculate')}</button>
                  </div>

                  {!isValid && (
                    <p className="text-xs text-red-600 mt-2">
                      {t('roiCalculator.calculator.step2.validation.required', { 
                        type: focus === t('roiCalculator.calculator.step1.options.econsult') ? "weekly volume" : "weekly volume and average time per unit"
                      })}
                    </p>
                  )}
                </div>
              )}

              {/* Step 3: Results */}
              {step === 3 && (
                <div>
                  <h2 className="text-2xl font-bold mb-1">{t('roiCalculator.calculator.step3.title')}</h2>
                  <p className="text-xs text-slate-500 mb-4">
                    {t('roiCalculator.calculator.step3.inputsUsed', {
                      volume,
                      time: needsTime ? timeMin : "—",
                      automation: Math.round(automationFactor*100),
                      noshow: noshow ? `, no-show=${noshow}%` : ''
                    })}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {resultCards.map((c, i) => (
                      <div key={i} className="rounded-2xl border border-slate-200 p-4">
                        <p className="text-sm text-slate-600">{c.label}</p>
                        <p className="text-2xl font-bold">{c.value}</p>
                        {c.annual && (
                          <p className="text-xs text-slate-500 mt-1">≈ {c.annual} / year</p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="rounded-2xl bg-[#F7F8FD] border border-[#1F2B59]/10 p-4 mb-6">
                    <p className="text-sm text-slate-700"><span className="font-semibold">{t('roiCalculator.calculator.step3.summary.focus')}</span> {focus || "—"}</p>
                    <p className="text-sm text-slate-700"><span className="font-semibold">{t('roiCalculator.calculator.step3.summary.automationLevel')}</span> {automationLevel}</p>
                  </div>

                  <div className="flex flex-wrap gap-3 mb-6">
                    <button onClick={() => setStep(2)} className="px-5 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition">{t('roiCalculator.calculator.step3.buttons.editInputs')}</button>
                    <button onClick={() => { setFocus(""); setStep(1); }} className="px-5 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition">{t('roiCalculator.calculator.step3.buttons.changeFocus')}</button>
                  </div>

                  {/* Gated form */}
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <h3 className="text-xl font-semibold mb-2">{t('roiCalculator.calculator.step3.form.title')}</h3>
                    <p className="text-slate-600 mb-4">{t('roiCalculator.calculator.step3.form.subtitle')}</p>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input 
                        type="text" 
                        required 
                        placeholder={t('roiCalculator.calculator.step3.form.name')} 
                        className="w-full border rounded px-3 py-2" 
                        value={form.name} 
                        onChange={(e) => setForm({ ...form, name: e.target.value })} 
                      />
                      <input 
                        type="email" 
                        required 
                        placeholder={t('roiCalculator.calculator.step3.form.email')} 
                        className="w-full border rounded px-3 py-2" 
                        value={form.email} 
                        onChange={(e) => setForm({ ...form, email: e.target.value })} 
                      />
                      <input 
                        type="text" 
                        required 
                        placeholder={t('roiCalculator.calculator.step3.form.organization')} 
                        className="w-full border rounded px-3 py-2" 
                        value={form.org} 
                        onChange={(e) => setForm({ ...form, org: e.target.value })} 
                      />
                      <input 
                        type="tel" 
                        placeholder={t('roiCalculator.calculator.step3.form.phone')} 
                        className="w-full border rounded px-3 py-2" 
                        value={form.phone} 
                        onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                      />
                      <div className="sm:col-span-2 flex items-center justify-between mt-1">
                        <span className="text-xs text-slate-500">{t('roiCalculator.calculator.step3.form.agreement')}</span>
                        <button type="submit" className="bg-[#7AC943] text-white px-6 py-3 rounded-lg shadow hover:bg-green-600 transition">{t('roiCalculator.calculator.step3.form.submit')}</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Thank-you view
            <div className="text-center py-12">
              <h3 className="text-2xl font-bold mb-2">{t('roiCalculator.calculator.thankYou.title')}</h3>
              <p className="text-slate-600 max-w-2xl mx-auto">{t('roiCalculator.calculator.thankYou.message')}</p>
              <div className="mt-6 flex gap-3 justify-center">
                <button 
                  onClick={handleBookDemo}
                  className="text-center bg-[#395091] text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-[#2c3b6f] transition"
                >
                  {t('common.bookDemo')}
                </button>
                <button 
                  onClick={() => { setSubmitted(false); setStep(1); setFocus(""); }} 
                  className="text-center bg-white border border-slate-200 font-semibold px-6 py-3 rounded-lg hover:bg-slate-50 transition"
                >
                  {t('roiCalculator.calculator.thankYou.runAnotherScenario')}
                </button>
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* TRUSTED & COMPLIANT */}
      <Section
        id="trusted"
        className="border-t border-slate-200 py-12 sm:py-14"
      >
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 text-center">
          {t("ai.trustedCompliant.title")}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.hipaaAligned")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <FileCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.soc2Type2")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Lock className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.ssoSaml")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.gdprDpf")}
            </span>
          </div>
        </div>
      </Section>
    </div>
  )
}

export default ROI
